import { LazyQueryResult } from '@apollo/client';
import {
  useIonViewDidEnter,
  useIonViewWillEnter,
  useIonViewWillLeave,
} from '@ionic/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApolloClearCacheForQuery } from '../../api/cache.hooks';
import {
  Attachment,
  ChatMessage,
  ChatSource,
  ChatStatus,
  Exact,
  ListChatMessagesDocument,
  ListChatMessagesInput,
  ListChatMessagesQuery,
  ListChatMessagesQueryVariables,
  SubscribeToChatMessagesDocument,
  SubscribeToChatMessagesSubscriptionResult,
  SubscribeToChatMessagesSubscriptionVariables,
  useListChatMessagesLazyQuery,
} from '../../api/graphql/generated';
import { useGetChatSubscription } from '../../api/hooks/chat.hooks';
import { useAutoMarkNotificationReadBySource } from '../../api/hooks/event-notifications.hooks';
import {
  useCheckPushNotificationsDenied,
  usePushNotificationsUnavailableToastLazy,
} from '../../api/hooks/push-notification.hooks';
import { useApolloSharedSubscription } from '../../api/subscription/apollo-subscription.manager';
import { useCurrentUserProp } from '../../api/user.hooks';
import { useIsViewing } from '../../util/hooks';
import { logger } from '../../util/logger';
import { useRegisterBackButtonCallback } from '../header/back-button';

interface ClearChatMessagesCacheOnLoadOptions {
  chatId: string;
  threadId?: string;
}

interface ListChatMessageHelperOptions {
  chatId: string;
  threadId?: string;
  cursor?: CursorVal;
  contentRef: React.RefObject<HTMLIonContentElement>;
  scroll?: {
    auto?: {
      enabled?: boolean;
      direction: AutoScrollDirection;
    };
  };
  clearCacheOnLoad?: boolean;
  refetchOnViewEnter?: boolean;
}

type AutoScrollDirection = 'bottom' | 'top';
type CursorVal = string | undefined | null;

interface ListChatMessageHelperResult {
  queryResult: ChatMessageListQueryResult;
  waitingOnFirstLoad: boolean;
  getMoreMessages: (cursor?: CursorVal) => Promise<void>;
  hasNewMessage: boolean;
  onNewMessageClick: () => Promise<void>;
  onScrollEnd: () => Promise<void>;
  getMoreForInfiniteScroll: (
    cursor?: CursorVal,
    e?: CustomEvent
  ) => Promise<void>;
}

type ChatMessageListQueryResult = LazyQueryResult<
  ListChatMessagesQuery,
  Exact<{ input: ListChatMessagesInput }>
>;

const useClearChatMessagesCacheOnLoadLazy = (
  options: ClearChatMessagesCacheOnLoadOptions
): (() => void) => {
  const clearApolloCache = useApolloClearCacheForQuery();

  const { chatId, threadId } = options;

  return useCallback(() => {
    const variables: ListChatMessagesQueryVariables = {
      input: {
        chatId,
      },
    };

    if (threadId) {
      variables.input.threadId = threadId;
    }

    clearApolloCache<ListChatMessagesQuery, ListChatMessagesQueryVariables>({
      query: ListChatMessagesDocument,
      variables,
      data: {
        listChatMessages: {
          // when writing to the cache lets set the array to null so as we make
          // checks on when to load items or scroll we can skip the one instance where we
          // are clearing the cache, but are still causing the apollo callback to fire. this
          // functionality was not needed before recent apollo upgrades
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          items: null as any,
          prevCursor: null,
          nextCursor: null,
        },
      },
      runGarbageCollection: true,
    });
  }, [clearApolloCache, chatId, threadId]);
};

const useListChatMessageQueryHelper = (
  options: ListChatMessageHelperOptions
): ListChatMessageHelperResult => {
  const { chatId, threadId, cursor, contentRef, refetchOnViewEnter } = options;
  const isViewing = useIsViewing();
  const userId = useCurrentUserProp('id');

  const [waitingOnFirstLoad, setWaitingOnFirstLoad] = useState<boolean>(true);
  const [hasNewMessage, setHasNewMessage] = useState<boolean>(false);

  const autoScrollEnabled = options.scroll?.auto?.enabled;
  const autoScrollDirection = options.scroll?.auto?.direction;
  const clearCacheOnLoad = options.clearCacheOnLoad;

  const autoScroll = useContentAutoScroll(
    contentRef,
    autoScrollDirection ?? 'bottom'
  );

  const clearCache = useClearChatMessagesCacheOnLoadLazy({
    chatId,
    threadId,
  });

  useRegisterBackButtonCallback(() => {
    clearCache();
    return true;
  });

  const [listChatMessages, queryResult] = useListChatMessagesLazyQuery({
    fetchPolicy: cursor ? 'network-only' : 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: {
      input: {
        chatId,
        cursor,
        limit: 10,
        threadId,
      },
    },
  });

  const { fetchMore, loading } = queryResult;

  /**
    This effect is to ensure that clearCache is invoked only upon
    entry into a component. A lazy query is necessary to ensure
    the cache is cleared first.
  */
  useEffect(() => {
    const execute = async () => {
      if (waitingOnFirstLoad) {
        if (clearCacheOnLoad) {
          clearCache();
        }
        const result = await listChatMessages();

        if (result?.data?.listChatMessages?.items) {
          if (autoScrollEnabled) {
            requestAnimationFrame(() => {
              autoScroll();
            });
          }
          setWaitingOnFirstLoad(false);
        }
      }
    };

    execute();
  }, [
    clearCache,
    clearCacheOnLoad,
    listChatMessages,
    waitingOnFirstLoad,
    autoScroll,
    autoScrollEnabled,
  ]);

  useIonViewWillEnter(() => {
    if (refetchOnViewEnter && !loading && !waitingOnFirstLoad) {
      listChatMessages();
    }
  }, [refetchOnViewEnter, loading, listChatMessages, waitingOnFirstLoad]);

  const getMoreMessages = useCallback(
    async (cursor: string | undefined | null = undefined) => {
      if (isViewing && fetchMore) {
        logger.info('fetching additional records');
        await fetchMore({
          variables: {
            input: {
              chatId,
              cursor,
              limit: 10,
              threadId,
            },
          },
        });
      }
    },
    [chatId, fetchMore, threadId, isViewing]
  );

  const subscriptionVariables: SubscribeToChatMessagesSubscriptionVariables = {
    input: {
      chatId,
    },
  };

  useApolloSharedSubscription<SubscribeToChatMessagesSubscriptionResult>({
    query: SubscribeToChatMessagesDocument,
    variables: subscriptionVariables,
    callbackOnly: true,
    callback: (result: SubscribeToChatMessagesSubscriptionResult) => {
      if (result.data?.subscribeToChatMessages) {
        onNewChatMessage(result.data?.subscribeToChatMessages);
      }
    },
    disablePreviousResultEmit: true,
  });

  const markEventNotificationsReadMutation =
    useAutoMarkNotificationReadBySource({
      variables: {
        input: {
          sourceIds: !threadId ? [chatId] : undefined,
          externalIds: threadId ? [threadId as string] : undefined,
        },
      },
    });

  const onNewMessageClick = useCallback(
    async (simulated = false) => {
      await getMoreMessages();

      if (simulated) {
        markEventNotificationsReadMutation();
      }

      await autoScroll();
    },
    [autoScroll, getMoreMessages, markEventNotificationsReadMutation]
  );

  const isNearEdge = useCallback(async () => {
    return checkContentScrollPositionWithin(
      contentRef,
      50,
      autoScrollDirection === 'bottom' ? true : false
    );
  }, [contentRef, autoScrollDirection]);

  const onNewChatMessage = useCallback(
    async (message: ChatMessage) => {
      if (chatId !== message.chatId) {
        return;
      } else if (threadId && message.threadId !== threadId) {
        return;
      }

      const { source, sourceId } = message;

      const isFromUser = source === ChatSource.USER;
      const isAuthorOfMessage = isFromUser && sourceId === userId;

      if (isAuthorOfMessage) {
        // if we are the author then piggy back on a simulated click event
        // which will refresh the data and take care of auto scrolling
        // note: the alternative to this is to rely on cache updates to trigger
        // a re-render which introduces some more complexity, however there are
        // helper cache functions written that are unused for right now. one issue
        // with the cache re-render is we need to auto scroll after the new message is
        // inserted but due to the async nature of responding to cache changes we
        // don't know when the new data is rendered to begin the auto scroll
        onNewMessageClick(true);
      } else {
        const isAlreadyViewingMessage = await isNearEdge();

        if (isAlreadyViewingMessage && isViewing) {
          onNewMessageClick(true);
        } else {
          setHasNewMessage(true);
          await getMoreMessages();
        }
      }
    },
    [
      userId,
      threadId,
      chatId,
      onNewMessageClick,
      getMoreMessages,
      isNearEdge,
      isViewing,
    ]
  );

  const onScrollEnd = useCallback(async () => {
    const isCloseToEdge = await isNearEdge();

    if (isCloseToEdge) {
      if (hasNewMessage) {
        setHasNewMessage(false);
        markEventNotificationsReadMutation();
      }
    }
  }, [hasNewMessage, markEventNotificationsReadMutation, isNearEdge]);

  const getMoreForInfiniteScroll = useCallback(
    async (cursor: string | undefined | null, e?: CustomEvent) => {
      await getMoreMessages(cursor);
      if (e) {
        const target = e.target as HTMLIonInfiniteScrollElement;
        target.complete();
      }
    },
    [getMoreMessages]
  );

  const result = useMemo((): ListChatMessageHelperResult => {
    return {
      queryResult,
      waitingOnFirstLoad,
      getMoreMessages,
      hasNewMessage,
      onNewMessageClick,
      onScrollEnd,
      getMoreForInfiniteScroll,
    };
  }, [
    queryResult,
    waitingOnFirstLoad,
    getMoreMessages,
    hasNewMessage,
    onNewMessageClick,
    onScrollEnd,
    getMoreForInfiniteScroll,
  ]);

  return result;
};

const useContentAutoScroll = (
  contentRef: React.RefObject<HTMLIonContentElement>,
  direction: 'top' | 'bottom'
): ((duration?: number) => Promise<void>) => {
  return useCallback(
    async (duration = 500) => {
      if (direction === 'bottom') {
        await contentRef.current?.scrollToBottom(duration);
      } else {
        await contentRef.current?.scrollToTop(duration);
      }
    },
    [contentRef, direction]
  );
};

const checkContentScrollPositionWithin = async (
  contentRef: React.RefObject<HTMLIonContentElement>,
  range = 50,
  reverse = false
): Promise<boolean> => {
  const scrollElement = await contentRef.current?.getScrollElement();
  if (scrollElement) {
    const { scrollHeight, scrollTop, clientHeight } = scrollElement;
    const { top } = scrollElement.getBoundingClientRect();
    if (reverse) {
      const positionBottom = scrollHeight - clientHeight - scrollTop - top;
      return positionBottom < range;
    } else {
      return scrollTop < range;
    }
  }
  return false;
};

interface UserChatNotificationToastResult {
  present: () => void;
  dismiss: () => void;
}

const useUserChatNotificationToastLazy = (
  chatId: string
): UserChatNotificationToastResult => {
  const [existingSubscription] = useGetChatSubscription(chatId);
  const checkPushNotificationsDenied = useCheckPushNotificationsDenied();
  const [present, dismiss] = usePushNotificationsUnavailableToastLazy();

  const display = useCallback(async () => {
    const isDenied = await checkPushNotificationsDenied();

    if (isDenied || existingSubscription === undefined) {
      present();
    }
  }, [checkPushNotificationsDenied, existingSubscription, present]);

  const result = useMemo(() => {
    return {
      present: display,
      dismiss,
    };
  }, [display, dismiss]);

  return result;
};

const useUserChatNotificationToast = (chatId: string) => {
  const { present, dismiss } = useUserChatNotificationToastLazy(chatId);

  useIonViewDidEnter(() => {
    present();
  }, [present]);

  useIonViewWillLeave(() => {
    dismiss();
  });
};

interface IsValidInputOptions {
  value: string;
  status: ChatStatus;
}

const useIsValidInput = (
  attachments?: Attachment[]
): ((options: IsValidInputOptions) => boolean) => {
  /**
   * For the given chat status, is the provided message
   * valid to be saved? For example, published messages can't be blank.
   */
  const isValidMessage = useCallback(
    (options: IsValidInputOptions) => {
      const { value, status } = options;
      if (status === ChatStatus.DRAFT) {
        return true;
      }
      // Published messages require text and/or attachments
      return (
        value.trim().length > 0 ||
        (attachments !== undefined && attachments.length > 0)
      );
    },
    [attachments]
  );

  return isValidMessage;
};

export {
  checkContentScrollPositionWithin,
  useClearChatMessagesCacheOnLoadLazy,
  useContentAutoScroll,
  useIsValidInput,
  useListChatMessageQueryHelper,
  useUserChatNotificationToast,
  useUserChatNotificationToastLazy,
};
